import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { api, isUnreachable, post } from './api.js';
import { applyTheme } from './theme.js';
import { forgetAll, LAST_ME, recall, remember } from './offline.js';

export type PublicUser = {
  id: string;
  name: string;
  first_name: string | null;
  last_name: string | null;
  employee_code: string | null;
  role: 'admin' | 'worker' | 'supervisor';
  phone: string | null;
  email: string | null;
  employment_type: 'full_time' | 'part_time' | null;
  /** Which of the two shifts. Always set; the column defaults to the first. */
  shift: 'first' | 'second';
  join_date: string | null;
  active: boolean;
  /** Contractor or employee. Deliberately does NOT carry the fee — that is admin-only. */
  engagement_type: 'contractor' | 'employee';
  designation_id: string | null;
};

export type Me = {
  user: PublicUser | null;
  session: { kind: 'admin' | 'worker'; mfa_verified: boolean; expires_at: string } | null;
  passkey?: { registered: boolean; since?: string; device_type?: string | null };
  theme?: 'system' | 'light' | 'dark';
  /** When this user's photograph was last set, or null for none. The URL is built from it. */
  photo_updated_at?: string | null;
};

type AuthState = {
  me: Me | null; // null while loading
  error: boolean;
  /** True when a signed-in session was rejected by the server (expired or revoked). */
  expired: boolean;
  /**
   * True when we are working from the last thing we knew because the server could not be
   * reached. The worker is still signed in — we simply have nothing fresh to show them.
   */
  offline: boolean;
  /** When that last known answer was true, for screens that say so out loud. */
  knownAt: string | null;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [error, setError] = useState(false);
  const [expired, setExpired] = useState(false);
  const [offline, setOffline] = useState(false);
  const [knownAt, setKnownAt] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const next = await api<Me>('/auth/me');
      setMe(next);
      if (next.user) {
        setExpired(false);
        if (next.theme) applyTheme(next.theme, { persistLocal: true });
        remember(LAST_ME, next);
      } else {
        // The server answered, and the answer is that nobody is signed in. That is authority.
        forgetAll();
      }
      setError(false);
      setOffline(false);
      setKnownAt(null);
    } catch (err) {
      // Not being able to ask is not the same as being told no. A worker who was signed in a
      // moment ago is still signed in; we just cannot prove it right now, so we carry on with
      // what we last knew and let the screens say how old it is.
      const last = isUnreachable(err) ? recall<Me>(LAST_ME) : null;
      if (last?.value.user) {
        setMe(last.value);
        setKnownAt(last.at);
        setOffline(true);
        setError(false);
        return;
      }
      setMe({ user: null, session: null });
      setError(true);
      setOffline(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await post('/auth/logout');
    } finally {
      setExpired(false);
      setOffline(false);
      setKnownAt(null);
      // The next person to hold this phone is not this worker.
      forgetAll();
      setMe({ user: null, session: null });
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // The moment the phone has a network again, ask properly. A worker should not have to know to
  // pull down to refresh; the screen should simply stop being old.
  useEffect(() => {
    const onOnline = () => void refresh();
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [refresh]);

  // Any 401 while a user is known means the session ended: back to sign-in with a message.
  useEffect(() => {
    const onUnauthorized = () => {
      setMe((current) => {
        if (current?.user) {
          setExpired(true);
          setOffline(false);
          setKnownAt(null);
          forgetAll();
          return { user: null, session: null };
        }
        return current;
      });
    };
    window.addEventListener('siva:unauthorized', onUnauthorized);
    return () => window.removeEventListener('siva:unauthorized', onUnauthorized);
  }, []);

  return (
    <AuthContext.Provider value={{ me, error, expired, offline, knownAt, refresh, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth outside AuthProvider');
  return ctx;
}
